import React, { useState, useEffect, useImperativeHandle } from "react";
import { View, StyleSheet, Platform, ScrollView, Text } from "react-native";

import {
  hasItems,
  sortByRelevance,
  escapeRegexp,
  isWeb,
} from "../../../../../helpers/utils";

import IntegrationData from "./IntegrationData";
import ItemNotFound from "../../../common/ItemNotFound";
import ElementsList from "../../../elements/list/ElementsList";
import ElementsHelper from "../../../elements/ElementsHelper";
import AppTheme from "../../../AppTheme";
import appStyles from "../../../../appStyles";

const TrackElementPicker = React.forwardRef(
  (
    {
      timeslot,
      journalContext,
      searchTerm = "",
      initialNumToRender,
      trackMultiple = false,
      selectedCount = 0,
      showIcons = true,
      selectedElementsIds = [],
      enableMultipleTracking = () => {},
    },
    ref,
  ) => {
    useImperativeHandle(ref, () => ({
      refresh,
    }));

    const { controller, swapCurrentActivity, addNewElement, showEditOptions } =
      journalContext || {};

    const [selectedSourceId, setSelectedSourceId] = useState(0);
    const [allItems, setAllItems] = useState([]);
    const [itemsList, setItemsList] = useState([]);

    const elementType = "element";
    const title = trackMultiple
      ? "Select Up to 3 Elements to Track"
      : (timeslot.confirmed ? "Replace " : "Choose ") + elementType;

    // ------------------------------------- METHODS -------------------------------------
    useEffect(() => refresh(), [timeslot]);
    useEffect(() => filterResults(allItems), [searchTerm]);

    // ------------------------------------- METHODS -------------------------------------
    const refresh = () => {
      if (!timeslot || !timeslot.id) return;

      const elements = fetchElements();
      if (selectedElementsIds) {
        elements.forEach((el) => {
          el.is_selected = selectedElementsIds.includes(el.id) ? 1 : 0;
        });
      }

      const data = ElementsHelper.sortElements(
        elements,
        "is_predicted|checked|is_favorite",
      );
      setAllItems(data);
      filterResults(data);
    };

    const fetchElements = () => {
      let allItemsData = ElementsHelper.filterResults(controller, {
        sortBy: "nosort",
      });

      // Check which itemsList were predicted
      if (hasItems(timeslot.predicted_activities)) {
        const predictions = timeslot.predicted_activities
          .slice(0, 3)
          .reduce((acc, p) => {
            acc[p.id] = p;
            return acc;
          }, {});

        allItemsData.forEach((item) => {
          item.is_favorite = item.is_favorite ? item.is_favorite : 0;
          item.was_predicted = 0;

          if (predictions[item.id]) {
            item.probability = predictions[item.id].prob;
            item.was_predicted = 1;
            item.was_first_option =
              timeslot.predicted_activities[0].id == item.id;
            item.source = predictions[item.id].source;
          }
        });

        allItemsData.sort((a, b) => {
          if (a.was_predicted != b.was_predicted) {
            return a.was_predicted > b.was_predicted ? -1 : 1;
          }
          if (a.was_predicted) {
            if (a.probability == b.probability) {
              return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
            }
            return a.probability > b.probability ? -1 : 1;
          }
          if (a.is_favorite == b.is_favorite) {
            return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
          }
          return (a.is_favorite || 0) < (b.is_favorite || 0) ? 1 : -1;
        });
      } else {
        allItemsData.sort((a, b) => {
          if (a.is_favorite == b.is_favorite) {
            return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
          }
          return (a.is_favorite || 0) > (b.is_favorite || 0) ? -1 : 1;
        });
      }

      return allItemsData;
    };

    const filterResults = (allItemsData) => {
      let result = [...allItemsData];

      // Multi terms search
      if (searchTerm) {
        const pattern = new RegExp(
          escapeRegexp(searchTerm).split(" ").join("|"),
          "gi",
        );
        result = result.filter((a) => {
          return a.name && a.name.match(pattern);
        });
      }

      setItemsList(sortByRelevance(result, searchTerm));
    };

    const updateSelectedPrediction = (sourceId) => {
      setSelectedSourceId(sourceId);
      scrollViewRef.scrollToEnd({ animated: true });
    };

    return (
      <View style={styles.container}>
        <ScrollView
          contentContainerStyle={appStyles.scrollViewContent}
          keyboardShouldPersistTaps="handled"
        >
          {trackMultiple ? null : (
            <Text style={styles.multitasking} onPress={enableMultipleTracking}>
              Multitasking?
            </Text>
          )}

          {hasItems(itemsList) ? (
            <ElementsList
              title={title}
              headerStyle={styles.header}
              initialNumToRender={initialNumToRender}
              controller={controller}
              elements={itemsList}
              contentType={elementType}
              onElementSelected={swapCurrentActivity}
              showEditOptions={showEditOptions}
              disableToggle={true}
              maxToSelect={trackMultiple ? 3 : 0}
              selectedCount={selectedCount}
              hideActions={true}
              showIcons={showIcons}
              isTrackDetailsPage={true}
              keyboardShouldPersistTaps="handled"
              listStyle={isWeb ? {} : { paddingBottom: "70%" }}
            />
          ) : null}

          {/* 2022.06.30 From 'Track Detail', when searching, show 'Create & Add [Search Query]' button as the last item 
          of search results, even if there is an exact text match */}
          {!!searchTerm ? (
            <ItemNotFound
              hasResults={hasItems(itemsList)}
              name={searchTerm}
              type={elementType}
              create={() => addNewElement(searchTerm)}
            />
          ) : null}

          {timeslot.integration_data ? (
            <IntegrationData
              sourceId={selectedSourceId}
              updateSelectedPrediction={updateSelectedPrediction}
            />
          ) : null}
        </ScrollView>
      </View>
    );
  },
);

export default TrackElementPicker;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginTop: -2,
    paddingTop: 8,

    borderTopColor: "#CDCED2",
    borderTopWidth: 0.5,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,

    shadowOpacity: 0.05,
    shadowOffset: { width: 0, height: -2 },
    shadowColor: "#000",
    shadowRadius: 3,
    elevation: 3,

    backgroundColor: "#FFF",
  },

  header: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 16,
    lineHeight: 20,
    letterSpacing: 1,
    color: AppTheme.almostBlack,
  },
  multitasking: {
    position: "absolute",
    right: 8,
    top: 6,
    zIndex: 100,
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 12,
    lineHeight: 15,
    letterSpacing: 0.5,
    color: AppTheme.DEFAULT_COLOR,
  },
});
